import type {
  EvaluationPlanAttachment,
  EvaluationPlanAttachmentSlot,
  EvaluationPlanDraft,
  EvaluationStandardDraft,
} from '../types/evaluationPlan';
import {
  formatPlanLinkedStandardsSummary,
  formatPlanSummary,
  getEvaluationPlanAttachment,
} from '../types/evaluationPlan';
import {
  parseEvaluationPlanWebDocumentData,
  webDocumentHasWhiteFieldContent,
} from '../types/evaluationPlanWebDocument';
import { sheetCanvasDocumentHasContent } from '../types/evaluationPlanSheetCanvas';
import {
  getSheetCanvasFromAttachment,
  isCriterionTableWebDocumentAttachment,
  isShareLinkAttachment,
  isSheetCanvasAttachment,
  isWebDocumentAttachment,
} from './evaluationPlanAttachment';
import { formatPresentationLayoutBytes } from './programPresentationList';

export type EvaluationPlanDocumentListItem = {
  listKey: string;
  planId: string;
  categoryName: string;
  slot: EvaluationPlanAttachmentSlot;
  planSummary: string;
  linkedStandardsSummary: string;
  kindLabel: string;
  contentLabel: string;
  isEmptyContent: boolean;
  registeredAtLabel: string;
  dataBytes: number;
  attachment: EvaluationPlanAttachment;
  sortCategoryOrder: number;
  sortPlanOrder: number;
};

function isListableAttachment(attachment: EvaluationPlanAttachment | null | undefined): attachment is EvaluationPlanAttachment {
  if (!attachment) return false;
  if (attachment.kind === 'placeholder') return false;
  return true;
}

function formatRegisteredAtLabel(registeredAt: string): string {
  const trimmed = registeredAt.trim();
  if (!trimmed) return '—';
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function estimateAttachmentBytes(attachment: EvaluationPlanAttachment): number {
  try {
    return new TextEncoder().encode(JSON.stringify(attachment)).length;
  } catch {
    return 0;
  }
}

function resolveAttachmentKindLabel(attachment: EvaluationPlanAttachment, slot: EvaluationPlanAttachmentSlot): string {
  if (isShareLinkAttachment(attachment)) return '공유 링크';
  if (slot === 'sheet' && isSheetCanvasAttachment(attachment)) return 'A4 캔버스';
  if (isWebDocumentAttachment(attachment)) return '웹 문서';
  if (attachment.kind === 'file') return '파일';
  if (attachment.kind === 'form') return '양식';
  return attachment.kind;
}

function resolveAttachmentContentStatus(
  attachment: EvaluationPlanAttachment,
  slot: EvaluationPlanAttachmentSlot
): { label: string; isEmpty: boolean } {
  if (isShareLinkAttachment(attachment)) {
    const hasUrl = !!attachment.share_url?.trim();
    return hasUrl ? { label: '링크 있음', isEmpty: false } : { label: '링크 없음', isEmpty: true };
  }
  if (slot === 'sheet' && isSheetCanvasAttachment(attachment)) {
    const doc = getSheetCanvasFromAttachment(attachment);
    const hasContent = doc ? sheetCanvasDocumentHasContent(doc) : false;
    return hasContent ? { label: '내용 있음', isEmpty: false } : { label: '비어 있음', isEmpty: true };
  }
  if (isCriterionTableWebDocumentAttachment(attachment) || isWebDocumentAttachment(attachment)) {
    const raw = attachment.form_data?.web_document;
    const parsed = parseEvaluationPlanWebDocumentData(raw);
    if (!parsed) return { label: '형식 오류', isEmpty: true };
    const hasContent = webDocumentHasWhiteFieldContent(parsed);
    return hasContent ? { label: '내용 있음', isEmpty: false } : { label: '비어 있음', isEmpty: true };
  }
  return { label: '—', isEmpty: false };
}

function getPlanLinkedStandardsSummary(plan: EvaluationPlanDraft, standards: EvaluationStandardDraft[]): string {
  return formatPlanLinkedStandardsSummary(plan, standards);
}

export function buildEvaluationPlanDocumentListItems(params: {
  slot: EvaluationPlanAttachmentSlot;
  plans: EvaluationPlanDraft[];
  standards: EvaluationStandardDraft[];
  categoryOrder: string[];
}): EvaluationPlanDocumentListItem[] {
  const { slot, plans, standards, categoryOrder } = params;
  const categoryOrderMap = new Map(categoryOrder.map((name, index) => [name, index]));
  const items: EvaluationPlanDocumentListItem[] = [];

  plans.forEach((plan) => {
    const attachment = getEvaluationPlanAttachment(plan, slot);
    if (!isListableAttachment(attachment)) return;
    if (attachment.slot && attachment.slot !== slot) return;
    if (slot === 'criterion' && isSheetCanvasAttachment(attachment)) return;

    const content = resolveAttachmentContentStatus(attachment, slot);
    const categoryName = (plan.category_name ?? '').trim() || '—';
    items.push({
      listKey: `${plan.id}:${slot}`,
      planId: plan.id,
      categoryName,
      slot,
      planSummary: formatPlanSummary(plan),
      linkedStandardsSummary: getPlanLinkedStandardsSummary(plan, standards),
      kindLabel: resolveAttachmentKindLabel(attachment, slot),
      contentLabel: content.label,
      isEmptyContent: content.isEmpty,
      registeredAtLabel: formatRegisteredAtLabel(attachment.registered_at),
      dataBytes: estimateAttachmentBytes(attachment),
      attachment,
      sortCategoryOrder: categoryOrderMap.get(categoryName) ?? 9999,
      sortPlanOrder: plan.sort_order ?? 0,
    });
  });

  return items.sort((a, b) => {
    if (a.sortCategoryOrder !== b.sortCategoryOrder) return a.sortCategoryOrder - b.sortCategoryOrder;
    if (a.sortPlanOrder !== b.sortPlanOrder) return a.sortPlanOrder - b.sortPlanOrder;
    return a.planId.localeCompare(b.planId);
  });
}

export function formatEvaluationPlanDocumentDataBytes(bytes: number): string {
  if (bytes <= 0) return '—';
  return formatPresentationLayoutBytes(bytes);
}
